import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Chip, Tooltip, Typography } from '@mui/material';
import { Forum as ConversationIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { NO_CONVERSATION_ACCESS } from '../task-queue/SourceThreadLink';
import { formatTimestampDateForDisplay } from '../../utils/formatters';
import { useBlockHidden } from '../../hooks/queries/usePharmacyVisibility';
import { PHOX_ENTRY_VISIBILITY_KEY } from '../../constants/phoxEntry';

/** Shown when the server could not read the order's linked requests (`phoxEntrySourcesUnavailable`). */
export const LINKED_REQUESTS_UNAVAILABLE = 'Linked requests could not be read.';

/** One `metadata.phoxEntrySources[]` record (backend `PhoxEntrySource`, ADR-109). */
export interface PhoxEntrySource {
  entryId: string;
  sourceType: string;
  threadId: string | null;
  linkedAt: string;
}

/**
 * PRX-143 — an order entered from the PHOX Entry queue, or linked to a request
 * while in progress (WP-15), keeps its link back to the request: one chip per
 * source on `metadata.phoxEntrySources`, naming the channel and the date it was
 * linked and opening the Communication Center thread. Never the message text.
 * Nothing for any other order. Opening the thread needs `viewPatients` (same
 * rule as a task's thread link).
 *
 * `unavailable`: the server could not read the linked requests. That is said in
 * plain words next to whatever sources the order still carries, so it never
 * looks like an order with no linked request. Not shown where the pharmacy
 * hides PHOX Entry (PRX-54): it has no linked requests to lose.
 */
const sourceLabel = (s: PhoxEntrySource): string => {
  const date = formatTimestampDateForDisplay(s.linkedAt);
  return date ? `From text · ${date}` : 'From text';
};

export const PhoxEntrySourceChips: React.FC<{
  metadata?: { phoxEntrySources?: PhoxEntrySource[] } | null;
  unavailable?: boolean;
  pharmacyId?: string;
}> = ({ metadata, unavailable, pharmacyId }) => {
  const { customRole, hasCustomPermission } = useAuth();
  const phoxEntryHidden = useBlockHidden(PHOX_ENTRY_VISIBILITY_KEY, pharmacyId);
  const sources = Array.isArray(metadata?.phoxEntrySources) ? metadata!.phoxEntrySources! : [];
  const showUnavailable = unavailable === true && !phoxEntryHidden;
  if (sources.length === 0 && !showUnavailable) return null;
  const canOpen = !customRole || hasCustomPermission('viewPatients');

  return (
    <>
      {sources.map((s) =>
        canOpen && s.threadId ? (
          <Chip
            key={s.entryId}
            size="small"
            variant="outlined"
            icon={<ConversationIcon />}
            label={sourceLabel(s)}
            component={RouterLink}
            to={`/communications?thread=${encodeURIComponent(s.threadId)}`}
            clickable
          />
        ) : (
          <Tooltip key={s.entryId} title={s.threadId ? NO_CONVERSATION_ACCESS : ''}>
            <Chip size="small" variant="outlined" icon={<ConversationIcon />} label={sourceLabel(s)} />
          </Tooltip>
        ),
      )}
      {showUnavailable && (
        <Typography variant="body2" color="warning.main" role="status">
          {LINKED_REQUESTS_UNAVAILABLE}
        </Typography>
      )}
    </>
  );
};

export default PhoxEntrySourceChips;
